/**
 * Mocked upstream data used by the in-process eval harness. The point: every task has
 * a verifiable single answer derived from this fixture, so deterministic equality
 * scoring is honest — no LLM-judge bias.
 *
 * Pet inventory deliberately exceeds the 20-item default page size (PRD M2) so that
 * the "how many pets are in inventory" task forces pagination — designed servers
 * paginate correctly, naive 1:1 servers return only the first page.
 */

export interface FixturePet {
  id: string;
  name: string;
  species: string;
  ownerEmail?: string;
}

export interface FixtureOrder {
  id: string;
  petId: string;
  customerEmail: string;
  status: "pending" | "approved" | "shipped" | "delivered";
  amountCents: number;
}

export const FIXTURE_PETS: FixturePet[] = Array.from({ length: 25 }, (_, i) => {
  const idx = i + 1;
  return {
    id: `pet-${String(idx).padStart(3, "0")}`,
    name: `Pet${idx}`,
    species: ["cat", "dog", "rabbit", "parrot", "turtle"][idx % 5] as string,
    ownerEmail: `owner${idx}@example.com`
  };
});

export const FIXTURE_ORDERS: FixtureOrder[] = Array.from({ length: 12 }, (_, i) => {
  const idx = i + 1;
  const status = (["pending", "approved", "shipped", "delivered"] as const)[idx % 4];
  return {
    id: `order-${String(idx).padStart(3, "0")}`,
    petId: `pet-${String(((idx - 1) % 25) + 1).padStart(3, "0")}`,
    customerEmail: `customer${idx}@example.com`,
    status,
    amountCents: idx * 1000
  };
});

export function findPet(id: string): FixturePet | undefined {
  return FIXTURE_PETS.find((p) => p.id === id);
}
export function findOrder(id: string): FixtureOrder | undefined {
  return FIXTURE_ORDERS.find((o) => o.id === id);
}

// ─── Library corpus (used by the "Public Library API" demo chip) ─────────────
// Same shape contract as the petstore: a known answer per task, with one
// pagination-required task so naive servers (no `pagination` declared) trip
// the same way they do on petstore.

export interface FixtureBook {
  id: string;
  title: string;
  author: string;
  subject: string;
  year: number;
}

export interface FixtureLoan {
  id: string;
  bookId: string;
  memberEmail: string;
  status: "active" | "returned" | "overdue";
}

const BOOK_SUBJECTS = ["history", "fiction", "science", "biography", "poetry"] as const;
const BOOK_AUTHORS = ["Asimov", "Hemingway", "Sagan", "Plath", "Borges"] as const;

export const FIXTURE_BOOKS: FixtureBook[] = Array.from({ length: 30 }, (_, i) => {
  const idx = i + 1;
  return {
    id: `bk-${String(idx).padStart(3, "0")}`,
    title: `Title ${idx}`,
    author: BOOK_AUTHORS[idx % BOOK_AUTHORS.length],
    subject: BOOK_SUBJECTS[idx % BOOK_SUBJECTS.length],
    year: 1900 + (idx * 7) % 125
  };
});

export const FIXTURE_LOANS: FixtureLoan[] = Array.from({ length: 18 }, (_, i) => {
  const idx = i + 1;
  const status = (["active", "returned", "overdue"] as const)[idx % 3];
  return {
    id: `loan-${String(idx).padStart(3, "0")}`,
    bookId: `bk-${String(((idx - 1) % 30) + 1).padStart(3, "0")}`,
    memberEmail: `member${idx}@example.com`,
    status
  };
});

export function findBook(id: string): FixtureBook | undefined {
  return FIXTURE_BOOKS.find((b) => b.id === id);
}
export function findLoan(id: string): FixtureLoan | undefined {
  return FIXTURE_LOANS.find((l) => l.id === id);
}
